import { Box, Button, Card, colors, Container, Divider, FormControlLabel, IconButton, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import toast from 'react-hot-toast';
import GeneralLoader from '../../components/GeneralLoader';
import EditIcon from '@mui/icons-material/Edit';
import KeyValueEditDialog from '../../components/KeyValueEditDialog';
import { getPaginatedItems } from '../../helpers/paginated';
import DeleteForever from '@mui/icons-material/DeleteForever';
import DialogConfirmation from '../../components/DialogConfirmation';
import ObjectEditDialog from './ObjectEditDialog';
import { exportData } from '../../helpers/exporter';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import { fetchWith401Check } from '../../helpers/http';

const pageWidth = { width: '100%', backgroundColor: 'white', padding: 2, marginTop: 1, marginBottom: 1 };
const pageWidthHeader = {
  width: '100%',
  backgroundColor: 'white',
  padding: 2,
  marginTop: 1,
  marginBottom: 1,
};
const LIMIT = 1;

const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;

const LibraryEditor = () => {
  const cookies = new Cookies();
  const [objectLibraries, setObjectLibraries] = React.useState('');
  const [selectedLibrary, setSelectedLibrary] = React.useState({ value: '', index: '' });
  const [selectedFieldToDelete, setSelectedFieldToDelete] = React.useState({});
  const [metadata, setMetadata] = React.useState({});
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [selectedObject, setSelectedObject] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);

  const updateObject = (data) => {
    if (metadata.index > -1) {
      const tempArray = objectLibraries;
      tempArray[metadata.index].id = data.key;
      tempArray[metadata.index].attributes.label = data.value;
      setObjectLibraries([...tempArray]);
      toast.success('Data updated, please click save button to apply the changes.');
      handleClose();
    } else {
      toast.error('No index found');
    }
  };

  const updateLibraries = () => {
    try {
      setIsLoading(true);
      const requestOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
      };
      fetchWith401Check(`${API_URL}/tableprops?type=object_libraries`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          if (response?.data?.data) {
            setObjectLibraries(response?.data?.data);
          }
        })
        .catch(function (err) {
          return toast.error(err.message);
        });

      setIsLoading(false);
    } catch (error) {
      toast.error(error.message);
      setIsLoading(false);
    }
  };

  const updateField = (e, index, key) => {
    const newValue = e.currentTarget.value;
    const tempArray = objectLibraries;
    tempArray[selectedLibrary.index].attributes.options[index][key] = newValue;
    setObjectLibraries([...tempArray]);
  };

  const updateObjectLibraries = async () => {
    try {
      setIsLoading(true);
      const requestOptions = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify({ data: { data: objectLibraries }, type: 'object_libraries' }),
      };

      fetchWith401Check(`${API_URL}/tableprops`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            toast.success('Data saved successfully');
            return response.json();
          }
          return Promise.reject(response);
        })
        .catch(function (err) {
          return toast.error(JSON.stringify(err));
        });
      setIsLoading(false);
    } catch (error) {
      toast.error(error.message);
      setIsLoading(false);
    }
  };

  const addNewField = () => {
    const tempArray = objectLibraries;
    const uuid = uuidv4();
    tempArray[selectedLibrary.index].attributes.options.push({ id: uuid, label: '', label_id: '' });
    setObjectLibraries([...tempArray]);
  };

  const addNewObject = () => {
    const tempArray = objectLibraries;
    tempArray.push({ id: 'please-change-this-id', type: 'libraries', attributes: { label: 'please-change-this-label', options: [] } });
    setObjectLibraries([...tempArray]);
  };

  const handleClose = () => {
    setConfirmationOpen(false);
    setDialogOpen(false);
  };

  const handleDelete = ({ isConfirmed }) => {
    const { target } = selectedFieldToDelete;

    if (!target) return toast.error('No target is specified, please try again');
    if (!isConfirmed) {
      setConfirmationOpen(true);
    } else {
      try {
        if (target === 'field') {
          const index = selectedFieldToDelete.option_index;
          if (index > -1) {
            const tempArray = objectLibraries;
            tempArray[selectedLibrary.index].attributes.options.splice(index, 1);
            setObjectLibraries([...tempArray]);
            toast.success('Data deleted, please click save button to apply the changes.');
          } else {
            toast.error('Index not found');
          }
          handleClose();
        }

        if (target === 'object') {
          const { index } = selectedFieldToDelete;
          if (index > -1) {
            const tempArray = objectLibraries;
            tempArray.splice(index, 1);
            setObjectLibraries([...tempArray]);
            toast.success('Data deleted, please click save button to apply the changes.');
          } else {
            toast.error('Index not found');
          }
          handleClose();
        }
      } catch (error) {
        handleClose();
        toast.error(error.message);
      }
    }
  };
  React.useEffect(() => {
    updateLibraries();
  }, []);

  const AddNewFieldButton = () => {
    return (
      <Button variant='contained' onClick={addNewField}>
        Add new field
      </Button>
    );
  };

  const AddNewObjectButton = () => {
    return (
      <Button variant='contained' onClick={addNewObject}>
        Add new object
      </Button>
    );
  };

  return (
    <Stack p={2} style={{ backgroundColor: colors.blue[100], minHeight: '100vh' }}>
      <Container maxWidth='lg'>
        <Card sx={pageWidthHeader}>
          <Stack direction='column'>
            <Stack my={2}>
              <Typography variant='h3' sx={{ textAlign: 'center', color: colors.grey[900] }}>
                Form Editor
              </Typography>
            </Stack>
            <Stack direction='column'>
              <Stack>
                <Typography variant='body2' sx={{ fontStyle: 'italic', textAlign: 'center', color: colors.grey[800] }}>
                  You can create your predefined library here
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Card>
        {isLoading && (
          <Stack direction='row' justifyContent='center' alignItems='center'>
            <Card sx={pageWidthHeader}>
              <GeneralLoader isLoading={isLoading} />
            </Card>
          </Stack>
        )}
        <Stack>
          <Card sx={pageWidthHeader}>
            <Stack direction='row' spacing={1} alignItems='center' mb={2}>
              <Typography>Select object to render</Typography>
              <AddNewObjectButton />
            </Stack>

            <RadioGroup
              aria-labelledby='demo-radio-buttons-group-label'
              value={selectedLibrary.value}
              name='radio-buttons-group'
              onChange={(e) => {
                const value = e.currentTarget.value;
                const index = _.findIndex(objectLibraries, (item) => {
                  return item.id === value;
                });
                setSelectedLibrary({ ...selectedLibrary, value: e.currentTarget.value, index: index });
              }}
            >
              {!_.isEmpty(objectLibraries) &&
                objectLibraries.map((item, index) => (
                  <Stack key={item.id} direction='row' alignItems='center' spacing={1}>
                    <FormControlLabel value={item.id} control={<Radio />} label={item?.attributes?.label} />
                    <EditIcon
                      sx={{ color: colors.blue[400], cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedObject(item);
                        setMetadata({ ...item, index: index });
                        setDialogOpen(true);
                      }}
                    />
                    <DeleteForever
                      sx={{ color: 'red', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedFieldToDelete({ ...item, index: index, target: 'object' });
                        handleDelete({ isConfirmed: false });
                      }}
                    />
                  </Stack>
                ))}
            </RadioGroup>
            <Box my={2}>
              <AddNewObjectButton />
            </Box>
          </Card>
        </Stack>

        {/* {JSON.stringify(objectLibraries)} */}

        <Stack>
          {!_.isEmpty(objectLibraries[selectedLibrary.index]) && (
            <Card key={objectLibraries[selectedLibrary.index].id} sx={pageWidthHeader}>
              <Stack direction='row' alignItems='center' spacing={1} mb={2}>
                <Typography variant='body1' sx={{ fontStyle: 'italic', textAlign: 'center', color: colors.grey[800], fontWeight: 700 }}>
                  ID: {objectLibraries[selectedLibrary.index]?.attributes?.label}
                </Typography>
                <AddNewFieldButton />
              </Stack>

              {objectLibraries[selectedLibrary.index]?.attributes?.options.length >= 100 ? (
                <Stack direction='column' spacing={1}>
                  <Typography variant='body2'>
                    The data length exceeding the maximum length of 100 rows that can be rendered (The total data length is: &nbsp;
                    {objectLibraries[selectedLibrary.index]?.attributes?.options.length}
                    ). You can download the data instead by click the download button below then report to your administrator if there are any changes need to
                    make.
                  </Typography>
                  <Button onClick={() => exportData(objectLibraries[selectedLibrary.index]?.attributes?.options)}>Download</Button>
                </Stack>
              ) : (
                <Stack alignItems='flex-start'>
                  {objectLibraries[selectedLibrary.index]?.attributes?.options?.map((option_item, option_index) => (
                    <Stack key={option_index} direction='row' spacing={2} alignItems='center'>
                      <Stack>
                        <TextField
                          onChange={(e) => updateField(e, option_index, 'key')}
                          required
                          margin='dense'
                          id='label'
                          label='ID'
                          type='text'
                          fullWidth
                          variant='outlined'
                          value={option_item.key}
                          sx={{ minWidth: 50 }}
                        />
                      </Stack>
                      <Stack>
                        <TextField
                          onChange={(e) => updateField(e, option_index, 'label')}
                          required
                          margin='dense'
                          id='label'
                          label='Label English'
                          type='text'
                          fullWidth
                          variant='outlined'
                          value={option_item.label}
                          sx={{ minWidth: 300 }}
                        />
                      </Stack>
                      <Stack>
                        <TextField
                          onChange={(e) => updateField(e, option_index, 'label_id')}
                          required
                          margin='dense'
                          id='label_id'
                          label='Label Indonesia'
                          type='text'
                          fullWidth
                          variant='outlined'
                          value={option_item.label_id}
                          sx={{ minWidth: 300 }}
                        />
                      </Stack>
                      <DeleteForever
                        sx={{ color: 'red', cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedFieldToDelete({ ...option_item, option_index: option_index, target: 'field' });
                          handleDelete({ isConfirmed: false });
                        }}
                      />
                    </Stack>
                  ))}
                  <Stack my={2}>
                    <AddNewFieldButton />
                  </Stack>
                </Stack>
              )}
            </Card>
          )}
        </Stack>
        <Card sx={pageWidthHeader}>
          <Stack my={2} spacing={2}>
            <Stack direction='column' alignItems='center' spacing={1}>
              <Box>
                <Button onClick={updateObjectLibraries} variant='outlined' size='large'>
                  Save
                </Button>
              </Box>
              <Typography variant='body2' sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                Important: all changes will be saved to this current state, please double check your entries.
              </Typography>
            </Stack>
          </Stack>
        </Card>
      </Container>

      <ObjectEditDialog open={dialogOpen} setOpen={setDialogOpen} metadata={metadata} update={updateObject} />
      <DialogConfirmation
        open={confirmationOpen}
        handleClose={() => setConfirmationOpen(false)}
        title='Deletion Confirmation'
        content='Are you sure want to delete this data?'
        closeButtonText='No'
        agreeButtonText='Yes'
        handleAgree={() => handleDelete({ isConfirmed: true })}
      />
    </Stack>
  );
};

export default LibraryEditor;
